import { Multiplayer } from "../../../shared/constants";
import { ClientAction, ServerMessage, SyncMessageType } from "../../../shared/messageTypes";
import { ConnectionToServer, SteamClient, WebRTCClient } from "./connectionToServer";
import { SteamConnectionPoller } from "./steamPeerConnection";
import { WebRTCConnectionPoller } from "./webrtcPeerConnection";

export type RemotePeerState = 'idle' | 'hosting' | 'connected' | 'closed';

export interface ConnectionToRemotePeer {
	host(): void;
	reconnectWithRetry(timeoutMs?: number): Promise<boolean>;
	send(message: ServerMessage): void;
	addListener(onServerMessage: (message: ServerMessage[]) => void): void;
	addCloseListener(listener: (intentional: boolean) => void): void;
	disconnectAndDispose(): void;
	getState(): RemotePeerState;
}

export class SteamConnectionToRemotePeer implements ConnectionToRemotePeer {
	connection: SteamClient;
	earlyMessages: ServerMessage[] = [];
	listeners: ((message: ServerMessage[]) => void)[] = [];
	closeListeners: ((intentional: boolean) => void)[] = [];
	//Owns the lobby, listen socket and channel for this session, for as long as it lasts
	private poller: SteamConnectionPoller;
	private state: RemotePeerState = 'idle';

	constructor(remoteConnection: SteamClient, poller: SteamConnectionPoller) {
		this.connection = remoteConnection;
		this.poller = poller;
	}

	getState(): RemotePeerState {
		return this.state;
	}

	host() {
		if (this.state === 'closed') {
			console.error("SteamConnectionToRemotePeer: Cannot host on a disposed connection");
			return;
		}
		if (this.state === 'hosting') {
			console.warn("SteamConnectionToRemotePeer: Already hosting, ignoring duplicate host() call");
			return;
		}

		this.state = 'hosting';
		try {
			this.state = 'connected';

			this.connection.addCloseListener((intentional) => {
				if (!intentional) {
					this.state = 'idle';
				}
				this.notifyClosed(intentional);
			});
			this.connection.addListener((message: ServerMessage[]) => {
				for (const listener of this.listeners) {
					listener(message);
				}
			});
			// Send any early messages that were queued before the connection was established
			for (const message of this.earlyMessages) {
				this.connection.send(message);
			}
			this.earlyMessages = [];

		} catch (error) {
			console.error('GameClient: Steam lobby creation failed', error);
		} finally {
			if (this.state === 'hosting') {
				this.state = 'idle';
			}
		}
	}

	async reconnectWithRetry(timeoutMs: number = Multiplayer.WaitForConnectionTimeout): Promise<boolean> {
		if (this.state === 'closed') return false;
		if (!this.connection) {
			console.error("SteamConnectionToRemotePlayer: No existing connection to reconnect");
			return false;
		}

		//The poller still holds this session's channel, lobby and listen socket, so the peer can
		//reconnect through it without re-hosting anything
		const success = await this.poller.reconnect(timeoutMs);
		if (!success) {
			console.error(`SteamConnectionToRemotePlayer: Steam didn't reconnect after ${timeoutMs / 1000 / 60} minutes, giving up`);
			return false;
		}
		this.state = 'connected';
		return true;
	}

	disconnectAndDispose(): void {
		this.state = 'closed';
		this.poller.dispose(); //leaves the steam lobby and closes this session's listen socket
		this.connection.close(); //reports the intentional close to listeners, before they're cleared below
		this.earlyMessages = [];
		this.listeners = [];
		this.closeListeners = [];
	}

	send(message: ServerMessage): void {
		if (this.state === 'connected' && this.connection) {
			this.connection.send(message);
		} else if (this.state !== 'closed') {
			this.earlyMessages.push(message);
		}
	}

	addListener(listener: (message: ServerMessage[]) => void): void {
		this.listeners.push(listener);
	}

	addCloseListener(listener: (intentional: boolean) => void): void {
		this.closeListeners.push(listener);
	}

	private notifyClosed(intentional: boolean) {
		for (const listener of this.closeListeners) {
			listener(intentional);
		}
	}
}

export class WebRTCConnectionToRemotePlayer implements ConnectionToRemotePeer {
	listeners: ((message: ServerMessage[]) => void)[] = [];
	closeListeners: ((intentional: boolean) => void)[] = [];
	connection: WebRTCClient;
	earlyMessages: ServerMessage[] = [];
	//Set only while a reconnect attempt is in flight, so disposal can abort it
	private reconnectPoller: WebRTCConnectionPoller | null = null;
	private state: RemotePeerState = 'idle';

	constructor(public signallingServerLobbyCode: string, remoteConnection: WebRTCClient) {
		this.signallingServerLobbyCode = signallingServerLobbyCode;
		this.connection = remoteConnection;
	}

	getState(): RemotePeerState {
		return this.state;
	}

	host() {
		if (this.state === 'closed') {
			console.error("WebRTCConnectionToRemotePlayer: Cannot host on a disposed connection");
			return;
		}
		if (this.state === 'hosting') {
			console.warn("WebRTCConnectionToRemotePlayer: Already hosting, ignoring duplicate host() call");
			return;
		}
		// Close any existing connection before re-hosting so the old peer is cleaned up
		// if (this.connection) {
		// 	this.connection.close();
		// }
		this.state = 'hosting';
		try {
			const remoteConnection = this.connection;
			this.state = 'connected';

			remoteConnection.addCloseListener((intentional) => {
				if (!intentional) {
					this.state = 'idle';
				}
				this.notifyClosed(intentional);
			});
			remoteConnection.addListener((message: ServerMessage[]) => {
				for (const listener of this.listeners) {
					listener(message);
				}
			});
			// Send any early messages that were queued before the connection was established
			for (const message of this.earlyMessages) {
				this.connection.send(message);
			}
			this.earlyMessages = [];
		} finally {
			// If we exited without reaching 'connected', reset so reconnectWithRetry can retry
			if (this.state === 'hosting') {
				this.state = 'idle';
			}
		}
	}

	async reconnectWithRetry(timeoutMs: number = Multiplayer.WaitForConnectionTimeout): Promise<boolean> {
		if (!this.signallingServerLobbyCode) {
			console.error("GameClient: No signalling server lobby code to re-open lobby with");
			return false;
		}

		const retryDelayMs = Multiplayer.ReconnectRetryDelay;
		const deadline = Date.now() + timeoutMs;
		while (Date.now() < deadline) {
			if (this.state === 'closed') return false;

			const poller = new WebRTCConnectionPoller();
			this.reconnectPoller = poller;
			try {
				const newConnection = await poller.openConnection(this.signallingServerLobbyCode, ((lobbyId: string) => {
					console.log("Reconnected to lobby with id " + lobbyId);
				}), Multiplayer.RetryConnectionTimeout);
				//via getState() because TS narrows this.state from the check above, not seeing that
				//disposal can land during the await
				if (this.getState() === 'closed') {
					//Disposed while this attempt was connecting. Nothing else holds the result.
					newConnection?.close();
					poller.dispose();
					this.reconnectPoller = null;
					return false;
				}
				if (newConnection) {
					this.connection.close(); //release the dropped connection being replaced
					this.connection = newConnection;
					this.host();
					if (this.state === 'connected') {
						//The peer owns the connection now, so the poller must not dispose it
						this.reconnectPoller = null;
						return true;
					}
				}
				console.warn("hostWebRTC attempt failed, retrying: got null connection");
			} catch (error) {
				console.warn("hostWebRTC attempt failed, retrying:", error);
			}
			//Every attempt that didn't hand off gets torn down, not just the ones that threw
			poller.dispose();
			if (this.reconnectPoller === poller) {
				this.reconnectPoller = null;
			}
			await new Promise(resolve => setTimeout(resolve, retryDelayMs));
		}
		return false;
	}

	disconnectAndDispose(): void {
		this.state = 'closed';
		this.reconnectPoller?.dispose(); //aborts a reconnect attempt in flight
		this.reconnectPoller = null;
		this.connection?.close(); //reports the intentional close to listeners, before they're cleared below
		this.earlyMessages = [];
		this.listeners = [];
		this.closeListeners = [];
	}

	send(message: ServerMessage): void {
		if (this.state === 'connected' && this.connection) {
			this.connection.send(message);
		} else if (this.state !== 'closed') {
			this.earlyMessages.push(message);
		}
	}

	addListener(listener: (message: ServerMessage[]) => void): void {
		this.listeners.push(listener);
	}

	addCloseListener(listener: (intentional: boolean) => void): void {
		this.closeListeners.push(listener);
	}

	private notifyClosed(intentional: boolean) {
		for (const listener of this.closeListeners) {
			listener(intentional);
		}
	}
}

export function wireRemotePeerToServerConnection(remoteConnection: ConnectionToRemotePeer, serverConnection: ConnectionToServer) {
	remoteConnection.addListener((message) => {
		//Pass through to server
		if (!serverConnection) {
			console.error("GameClient: Received peer messsage from remote client with no server connection!");
			return;
		}
		serverConnection.send(message);
	});
	remoteConnection.addCloseListener(() => {
		//Disconnect the connection to server, which will get reconnected later when the new peer connects
		//so server behaves appropriately, sends fresh state, etc.
		serverConnection?.send({
			messageType: ClientAction.PassThrough,
			connect: false,
			payload: null,
		});
	});
	serverConnection.addInterceptListener((message: ServerMessage[]) => {
		//Sepeate pass through from non pass through messages
		const nonPassThrough: ServerMessage[] = []
		for (const msg of message) {
			if (msg.messageType === SyncMessageType.PassThrough) {
				remoteConnection.send(msg);
			}
			else {
				nonPassThrough.push(msg);
			}
		}

		return nonPassThrough;
	});
}
